import { FC, useEffect } from 'react';
import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoginPage, ProtectedRoute } from '../../auth';
import * as ROUTES from '../../constants/routes';
import { AuthenticatedLayout } from '../../layouts/authenticated-layout';
import { DataRoomsPage } from '../../pages/data-rooms-page/data-rooms-page';
import { FileBrowserPage } from '../../pages/file-browser-page/file-browser-page';
import { FileDetailsPage } from '../../pages/file-details-page/file-details-page';
import { ProblematicFilesPage } from '../../pages/problematic-files-page/problematic-files-page';
import { SearchResultsPage } from '../../pages/search-results-page/search-results-page';
import { UserManagementPage } from '../../pages/user-management-page/user-management-page';
import { AppConfigProtected } from './app-config-protected';

export const AppRoutes: FC = () => {
  const location = useLocation();
  const { authState } = useOktaAuth();

  // Extract user information from the ID token claims
  const userInfo = authState?.idToken?.claims as
    | {
        sub?: string;
        email?: string;
        preferred_username?: string;
      }
    | undefined;

  useEffect(() => {
    if (userInfo) {
      Sentry.setUser({
        id: userInfo.sub,
        email: userInfo.email,
        username: userInfo.preferred_username,
      });
    }
  }, [userInfo]);

  useEffect(() => {
    Sentry.getCurrentScope().setTransactionName(location.pathname);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<LoginCallback />} />
      <Route path="/authorization-code/callback" element={<LoginCallback />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AuthenticatedLayout />}>
          <Route path="/" element={<DataRoomsPage />} />
          <Route path={ROUTES.DATA_ROOMS} element={<DataRoomsPage />} />
          <Route path={ROUTES.TREATY_BROWSER} element={<FileBrowserPage />} />
          <Route
            path={ROUTES.DIRECTORY_BROWSER}
            element={<FileBrowserPage />}
          />
          <Route path={ROUTES.FILE_DETAILS} element={<FileDetailsPage />} />
          <Route path={ROUTES.SEARCH} element={<SearchResultsPage />} />
          <Route
            path={ROUTES.USER_MANAGEMENT}
            element={
              <AppConfigProtected
                Component={UserManagementPage}
                grant="canManageUsers"
              />
            }
          />
          <Route
            path={ROUTES.PROBLEMATIC_FILES}
            element={
              <AppConfigProtected
                Component={ProblematicFilesPage}
                grant="canViewProblemFiles"
              />
            }
          />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          location.pathname !== '/login' &&
          location.pathname !== '/callback' &&
          location.pathname !== '/authorization-code/callback' ? (
            <Navigate to="/login" replace />
          ) : (
            <></>
          )
        }
      />
    </Routes>
  );
};
